import { FC } from "react";

import styled from "@emotion/styled";

const ProgressContainer = styled.div`
  width: 100%;
  max-width: 320px;
  margin-top: 1.5rem;
`;

const ProgressBarTrack = styled.div`
  width: 100%;
  height: 8px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 999px;
  overflow: hidden;
  position: relative;
`;

const ProgressBarFill = styled.div<{ progress: number }>`
  height: 100%;
  background: linear-gradient(
    90deg,
    var(--color-sunrise-blue),
    var(--color-sunrise-pink)
  );
  border-radius: 999px;
  transition: ${(props) =>
    props.progress === 0 ? "none" : "width 0.3s ease-out"};
  width: ${(props) => props.progress}%;
  min-width: ${(props) =>
    props.progress > 0 ? "8px" : "0"}; /* Ensure visible at start */
`;

const ProgressLabel = styled.div`
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.875rem;
  margin-top: 0.5rem;
  text-align: center;
`;

const ProgressPercentage = styled.div`
  color: white;
  font-weight: 600;
  font-size: 1.125rem;
  margin-top: 0.5rem;
  text-align: center;
`;

interface ProgressBarProps {
  progress: number;
  label?: string;
  showPercentage?: boolean;
}

/**
 * ProgressBar component for showing download/upload progress.
 * Displays a horizontal progress bar with optional percentage and label.
 *
 * @param progress - Progress percentage (0-100)
 * @param label - Optional label to show below progress (e.g., "45 MB / 100 MB")
 * @param showPercentage - Whether to show percentage text (default: true)
 */
export const ProgressBar: FC<ProgressBarProps> = ({
  progress,
  label,
  showPercentage = true
}) => {
  return (
    <ProgressContainer
      role="progressbar"
      aria-valuenow={progress}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={label || `${Math.round(progress)}% complete`}
    >
      <ProgressBarTrack>
        <ProgressBarFill progress={progress} />
      </ProgressBarTrack>
      {showPercentage && (
        <ProgressPercentage>{Math.round(progress)}%</ProgressPercentage>
      )}
      {label && <ProgressLabel>{label}</ProgressLabel>}
    </ProgressContainer>
  );
};
